import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Users, HardDrive, FileText, Share2, ShieldAlert, ArrowLeft } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { CardSpotlight } from '../components/aceternity/CardSpotlight';
import { AnimatedTabs } from '../components/aceternity/AnimatedTabs';
import { TiltCard } from '../components/aceternity/TiltCard';
import { Lamp } from '../components/aceternity/Lamp';
import { formatBytes, formatDate } from '../lib/utils';

export function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'users' | 'logs'>('users');

  const stats = useQuery({ queryKey: ['admin-stats'], queryFn: () => api.admin.stats(), enabled: user?.role === 'admin' });
  const users = useQuery({ queryKey: ['admin-users'], queryFn: () => api.admin.users(1), enabled: user?.role === 'admin' });
  const logs = useQuery({ queryKey: ['admin-logs'], queryFn: () => api.admin.auditLogs(1), enabled: user?.role === 'admin' });

  if (user?.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-text-muted">
        <ShieldAlert className="w-14 h-14" />
        <p className="text-lg">需要管理员权限</p>
        <button onClick={() => navigate('/files')} className="text-primary hover:underline text-sm">返回文件页</button>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-6 relative">
      <Lamp />
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono tracking-[0.35em] text-accent-cyan/80">ADMIN CONSOLE</span>
            <span className="h-px w-10 bg-gradient-to-r from-accent-cyan to-transparent" />
          </div>
          <h1 className="text-3xl font-black neon-text tracking-tight mt-0.5">管理后台</h1>
        </div>
        <motion.button whileTap={{ scale: 0.94 }} onClick={() => navigate('/files')} className="flex items-center gap-1 text-sm text-text-muted hover:text-text px-2.5 py-1.5 rounded-lg hover:bg-card transition-colors">
          <ArrowLeft className="w-4 h-4" /> 返回
        </motion.button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="用户数" value={String(stats.data?.users ?? '—')} delay={0} />
        <StatCard icon={FileText} label="文件数" value={String(stats.data?.files ?? '—')} delay={0.05} />
        <StatCard icon={HardDrive} label="已用存储" value={formatBytes(stats.data?.storageUsed ?? 0)} delay={0.1} />
        <StatCard icon={Share2} label="分享链接" value={String(stats.data?.shares ?? '—')} delay={0.15} />
      </div>

      {/* Tab 切换 */}
      <AnimatedTabs
        className="w-fit"
        active={tab}
        onChange={(id) => setTab(id as 'users' | 'logs')}
        tabs={[
          { id: 'users', label: '用户管理', icon: Users },
          { id: 'logs', label: '审计日志', icon: FileText },
        ]}
      />

      {tab === 'users' ? (
        <div className="bg-card/80 border border-border-soft rounded-2xl divide-y divide-border-soft overflow-hidden">
          {users.data?.items.map((u: any, idx: number) => (
            <div key={u.id} style={{ animationDelay: `${Math.min(idx * 0.03, 0.3)}s` }} className="stagger-item flex items-center gap-3 px-4 py-3 hover:bg-bg-secondary/50 transition-colors">
              <div className="w-9 h-9 rounded-full brand-gradient flex items-center justify-center text-white font-medium flex-shrink-0">
                {(u.name || u.email || '?').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text truncate">{u.name || '—'} {u.role === 'admin' && <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">管理员</span>}</p>
                <p className="text-xs text-text-muted truncate">{u.email}</p>
              </div>
              <div className="text-right text-xs text-text-muted flex-shrink-0">
                <p>已用 {formatBytes(u.used_bytes)}</p>
                <p>配额 {formatBytes(u.storage_quota_bytes)}</p>
              </div>
              <span className="text-xs text-text-muted flex-shrink-0 hidden sm:block">{formatDate(u.created_at)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card/80 border border-border-soft rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className="text-left text-xs text-text-muted border-b border-border-soft">
                  <th className="px-4 py-2.5">用户</th>
                  <th className="px-4 py-2">操作</th>
                  <th className="px-4 py-2">对象</th>
                  <th className="px-4 py-2">时间</th>
                </tr>
              </thead>
              <tbody>
                {logs.data?.items.map((l: any) => (
                  <tr key={l.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2.5 text-text-muted">{l.user_id ? l.user_id.slice(0, 8) : '—'}</td>
                    <td className="px-4 py-2.5 text-text">{l.action}</td>
                    <td className="px-4 py-2.5 text-text-muted">{l.target_type} {l.target_id ? `(${l.target_id.slice(0, 8)})` : ''}</td>
                    <td className="px-4 py-2.5 text-text-muted">{formatDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, delay = 0 }: { icon: any; label: string; value: string; delay?: number }) {
  return (
    <CardSpotlight className="animate-fadeInUp" >
      <TiltCard intensity={8} className="h-full">
        <div style={{ animationDelay: `${delay}s` }} className="relative overflow-hidden card-hover bg-card/70 border border-border-soft rounded-2xl p-4 soft-shadow h-full">
          <div className="absolute inset-x-0 top-0 h-0.5 brand-gradient" />
          <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full brand-gradient opacity-10 blur-xl" />
          <div className="flex items-center gap-2 text-text-muted text-xs mb-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-xl brand-gradient"><Icon className="w-4 h-4 text-white" /></span>
            {label}
          </div>
          <p className="text-2xl font-semibold text-text tabular-nums">{value}</p>
        </div>
      </TiltCard>
    </CardSpotlight>
  );
}
